import { useEffect } from "react";

// Fades `.reveal` elements up as they scroll into view (styles in reveal.css).
//
// Revealed elements get a `data-revealed` attribute rather than a class: React
// owns `className`, and re-rendering a component whose class changes would
// wipe a class added here.
//
// Elements that enter the viewport in the same observer callback — e.g. a row
// of project cards — get a small staggered delay so they cascade in; an
// element that enters on its own gets none. Each element reveals once.
//
// The observer's root is the viewport (null), which works on both layouts:
// on desktop the page scrolls inside .main-content, and intersection with the
// viewport already accounts for that clipping; on mobile the window scrolls.
const STAGGER_MS = 80;

const useScrollReveal = () => {
  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");
    if (elements.length === 0) return;

    // Older browsers without IntersectionObserver: just show everything.
    if (!("IntersectionObserver" in window)) {
      elements.forEach((el) => el.setAttribute("data-revealed", ""));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((entry) => entry.isIntersecting)
          .forEach((entry, i) => {
            entry.target.style.setProperty(
              "--reveal-delay",
              `${i * STAGGER_MS}ms`,
            );
            entry.target.setAttribute("data-revealed", "");
            observer.unobserve(entry.target);
          });
      },
      // Trigger slightly before the element's top edge is fully on screen.
      { rootMargin: "0px 0px -8% 0px", threshold: 0.1 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

export default useScrollReveal;
